import { FC, useMemo, useRef } from 'react'
import IconEmptyMap from 'assets/empty-map.svg?react'
import IconPhone from 'assets/phone.svg?react'
import IconMail from 'assets/post-mail.svg?react'
import IconYarcheFull from 'assets/yarche-full.svg?react'
import cn from 'classnames'
import IconArrowDown from 'remixicon/icons/Arrows/arrow-down-s-fill.svg?react'
import IconClose from 'remixicon/icons/System/close-line.svg?react'
import IconInformation from 'remixicon/icons/System/information-line.svg?react'
import { Button } from 'src/components'
import { LinkButton } from 'src/components/Link'
import { getFormatPhoneNumber } from 'src/helpers/string'
import { useCollapsible } from 'src/hooks/useCollapsible'
import { TDayOfWeek } from 'src/modules/terminal/types'
import { TerminalItem, TerminalScheduleItem } from 'src/schema'

import { TDaysMapValue } from '../ModalTerminal/types'
import { extractStreet } from './utils'

interface RouteSideBarProps {
  closeSideBar: () => void
  closestTerminals: TerminalItem[]
  handleChangeConfirmedTerminal: () => void
  handleChangeSelectedTerminal: (item: TerminalItem | null) => void
  mode: 'detail' | 'list'
  selectedTerminal: TerminalItem | null
}
export const RouteSideBar: FC<RouteSideBarProps> = ({
  closestTerminals,
  mode,
  closeSideBar,
  handleChangeConfirmedTerminal,
  handleChangeSelectedTerminal,
  selectedTerminal
}) => (
  <div
    className={cn(
      'absolute right-2 top-2 z-10 flex h-[calc(100%-16px)] w-[360px] flex-col gap-0.5 rounded-lg bg-surface px-2 shadow-[0px_8px_16px_0px_rgba(0,0,0,0.12),0px_4px_8px_0px_rgba(0,0,0,0.12),0px_0px_1px_0px_rgba(0,0,0,0.20)] max-md:bottom-0 max-md:left-0 max-md:top-[unset] max-md:max-h-[max(50%,300px)] max-md:w-full',
      {
        'max-md:top-0 max-md:h-full max-md:max-h-[unset] max-md:w-full max-md:bg-background max-md:p-0':
          mode === 'detail' && selectedTerminal
      }
    )}
  >
    {mode === 'detail' && selectedTerminal && (
      <DetailTerminal
        closeSideBar={closeSideBar}
        handleChangeConfirmedTerminal={handleChangeConfirmedTerminal}
        selectedTerminal={selectedTerminal}
      />
    )}
    {mode === 'list' && (
      <div className="flex max-h-full flex-grow flex-col">
        <div className="scrollbar-hide flex flex-grow flex-col overflow-y-scroll">
          <span className="sticky top-0 z-10 flex w-full items-center justify-between gap-2 bg-surface py-2 pl-3 text-title-md">
            <span>Выберите магазин</span>
            <Button
              appearance="secondary"
              className="size-10 min-w-[40px] max-md:hidden"
              contentContainerClassName="flex items-center justify-center"
              onClick={closeSideBar}
            >
              <IconClose size="sm" />
            </Button>
          </span>
          <div className="flex flex-grow flex-col gap-0.5">
            {closestTerminals?.length ? (
              closestTerminals?.map((terminal) => (
                <Button
                  appearance="transparent"
                  contentContainerClassName="w-full"
                  onClick={() => handleChangeSelectedTerminal(terminal)}
                  className={cn(
                    'rounded-md p-2 transition-colors focus-within:bg-state-on-surface-variant-focused hover:bg-state-on-surface-variant-hover',
                    {
                      'bg-primary-container': selectedTerminal && terminal.id === selectedTerminal.id
                    }
                  )}
                >
                  <span className="flex items-start justify-between">
                    <span className="flex w-[calc(100%-54px)] flex-col whitespace-normal text-left ">
                      <span className="max-w-full text-body-md text-on-surface">{terminal.name}</span>
                      <span className="text-label-sm text-on-surface-variant">{terminal.address}</span>
                    </span>
                    <span>
                      <IconYarcheFull size="xl" />
                    </span>
                  </span>
                </Button>
              ))
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-3">
                <IconEmptyMap size="xxl" />
                <div className="text-center text-headline-sm">
                  В области поиска <br /> нет доступных магазинов
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )}
  </div>
)

interface DetailTerminalProps {
  closeSideBar: RouteSideBarProps['closeSideBar']
  handleChangeConfirmedTerminal: RouteSideBarProps['handleChangeConfirmedTerminal']
  selectedTerminal: TerminalItem
}

const DetailTerminal: FC<DetailTerminalProps> = ({ selectedTerminal, closeSideBar, handleChangeConfirmedTerminal }) => {
  const daysMap = new Map<TDayOfWeek, TDaysMapValue>([
    ['mon', { name: 'Пн', index: 1 }],
    ['tue', { name: 'Вт', index: 2 }],
    ['wed', { name: 'Ср', index: 3 }],
    ['thu', { name: 'Чт', index: 4 }],
    ['fri', { name: 'Пт', index: 5 }],
    ['sat', { name: 'Сб', index: 6 }],
    ['sun', { name: 'Вс', index: 0 }]
  ])

  const getDay = (engDay: TDayOfWeek) => daysMap.get(engDay)

  const getIsToday = (dayOfWeek: TDayOfWeek) => {
    const day = getDay(dayOfWeek)
    const now = new Date()

    return day ? day.index === now.getDay() : false
  }
  const timeInfoNodeRef = useRef<HTMLDivElement>(null)
  const { height, isCollapsed, toggleCollapsed } = useCollapsible({
    nodeRef: timeInfoNodeRef
  })

  const scheduleByDayKey = useMemo(() => {
    if (!selectedTerminal || !selectedTerminal.schedule)
      return {} as Record<TerminalScheduleItem['dayOfWeek'], TerminalScheduleItem>

    return selectedTerminal.schedule.reduce(
      (acc, item) => {
        acc[item.dayOfWeek] = item

        return acc
      },
      {} as Record<TerminalScheduleItem['dayOfWeek'], TerminalScheduleItem>
    )
  }, [selectedTerminal])

  const todayBySchedule = selectedTerminal.schedule?.find((item) => getIsToday(item.dayOfWeek))?.to

  return (
    <div className="scrollbar-hide flex flex-col gap-4 overflow-y-scroll pb-2 max-md:rounded-xl max-md:bg-surface max-md:pb-4">
      <div className="sticky top-0 z-10 flex w-full items-center justify-between gap-2 bg-surface py-2 pl-4 text-title-md">
        <span className="text-headline-md text-on-surface">Ярче ({extractStreet(selectedTerminal.name)})</span>
        <Button
          appearance="secondary"
          className="size-10 min-w-[40px] max-md:hidden"
          contentContainerClassName="flex items-center justify-center"
          onClick={closeSideBar}
        >
          <IconClose size="sm" />
        </Button>
      </div>

      <div className="flex flex-col gap-4 px-4">
        <div className="flex flex-col">
          <span className="max-w-full text-body-md text-on-surface">{selectedTerminal.address}</span>
          <span className="text-label-sm text-on-surface-variant">
            {selectedTerminal.latitude}° {selectedTerminal.longitude}°
          </span>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-title-md">Время работы</div>
              {selectedTerminal?.schedule?.length && (
                <div className="flex cursor-pointer items-center gap-1 text-body-md" onClick={toggleCollapsed}>
                  {todayBySchedule ? `Открыто до ${todayBySchedule}` : 'Выходной'}
                  <IconArrowDown rotate={isCollapsed ? 0 : 180} size="xs" />
                </div>
              )}
            </div>
            <IconYarcheFull size="xl" />
          </div>
          <div
            ref={timeInfoNodeRef}
            className="flex justify-between gap-2 overflow-hidden transition-all"
            style={{ height }}
          >
            <div className="flex gap-2">
              <div className="flex flex-col gap-1 text-body-md">
                {!!selectedTerminal &&
                  !!selectedTerminal?.schedule?.length &&
                  Array.from(daysMap.keys()).map((dayKey) => {
                    const weekDay = daysMap.get(dayKey)
                    const scheduleDay = scheduleByDayKey[dayKey]
                    const isWeekend = !scheduleDay
                    const isToday = getIsToday(dayKey)

                    return (
                      <div
                        key={dayKey}
                        className={cn('flex items-center gap-4', {
                          'text-on-surface': !isToday && !isWeekend,
                          'text-positive': isToday,
                          'text-on-surface-variant': isWeekend
                        })}
                      >
                        {weekDay && <span className="w-6">{weekDay.name}</span>}
                        <span
                          className={cn('relative', {
                            'after:absolute after:-right-4 after:top-[calc((100%-8px)/2)] after:size-2 after:rounded-full after:bg-positive after:content-[""]':
                              isToday
                          })}
                        >
                          {isWeekend ? 'Выходной' : `${scheduleDay.from} – ${scheduleDay.to}`}
                        </span>
                      </div>
                    )
                  })}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <span className="text-title-md">Контакты</span>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <IconMail className="text-on-surface-variant" containerSize="nm" size="xs" />
              <div className="flex flex-col gap-1">
                {selectedTerminal?.emails?.map((email, index) => (
                  <div key={index}>
                    <LinkButton
                      onClick={() => {
                        window.location.href = `mailto:${email}`
                      }}
                    >
                      {email}
                    </LinkButton>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <IconPhone className="text-on-surface-variant" containerSize="nm" size="xs" />
              <div className="flex flex-col gap-1">
                {selectedTerminal?.phones?.map((phone, index) => (
                  <div key={index}>
                    <LinkButton
                      onClick={() => {
                        window.location.href = `tel:${getFormatPhoneNumber(phone)}`
                      }}
                    >
                      {getFormatPhoneNumber(phone)}
                    </LinkButton>
                  </div>
                ))}
              </div>
            </div>
            {selectedTerminal.comment && (
              <div className="flex items-center gap-2">
                <IconInformation className="text-on-surface-variant" containerSize="nm" size="xs" />
                <div>{selectedTerminal.comment}</div>
              </div>
            )}
          </div>
        </div>

        <Button appearance="primary" onClick={handleChangeConfirmedTerminal} size="lg">
          <span className="text-label-lg">Выбрать</span>
        </Button>
      </div>
    </div>
  )
}
